/**
 * The "Keywords in AI notes…" search (Trello #779), web half.
 *
 * The API filters; this module only HIGHLIGHTS what it matched, so it must
 * read the box the same way the server does:
 *
 * - `parseNotesTerms` mirrors `parse_notes_terms` in
 *   `packages/api/app/web/v2/imports/domain.py`. The two share one test table
 *   (`notesSearch.test.ts` / `test_domain_contact_filters.py`); change both.
 * - `findNotesRanges` mirrors the SQL predicate: a term matches at the START
 *   of a word, ignoring case (`~*`). Accents are not folded on either side —
 *   the notes are written in English.
 */

/** Most distinct terms kept — `MAX_NOTES_TERMS` on the server. */
export const MAX_NOTES_TERMS = 10;

const QUOTES = /[“”„‟″«»]/g;
const TOKEN = /"([^"]*)"|([^\s"]+)/g;
const ALNUM = /[\p{L}\p{N}]/u;
const KEEP_TRAILING = new Set(["+", "#"]);
// The only characters a `u`-flag RegExp lets you backslash. Escaping anything
// else (`\-`, `\#`, `\&`, `\'`) is a SyntaxError in Unicode mode, so the rest
// of the punctuation is copied as is — outside a class it is already literal.
const SYNTAX = new Set(Array.from("^$\\.*+?()[]{}|/"));

function isAlnum(char: string): boolean {
  return ALNUM.test(char);
}

function foldChar(char: string): string {
  return char.toLowerCase();
}

/** Case-insensitive key, as used for dedupe. */
export function foldNotesText(text: string): string {
  return Array.from(text, foldChar).join("");
}

function stripEdges(term: string): string {
  const chars = Array.from(term);
  let start = 0;
  let end = chars.length;
  while (start < end && !isAlnum(chars[start] as string)) start++;
  while (
    end > start &&
    !(isAlnum(chars[end - 1] as string) || KEEP_TRAILING.has(chars[end - 1] as string))
  ) {
    end--;
  }
  return chars.slice(start, end).join("");
}

/**
 * Split the box's text into terms; `[]` when none survive. Same rules as the
 * server: `"quoted phrase"` is one term (curly quotes too, an unmatched quote
 * is dropped), edge punctuation is stripped except a trailing `+`/`#`, terms
 * without a letter or digit are dropped, duplicates are dropped case- and
 * case-insensitively, at most {@link MAX_NOTES_TERMS}.
 */
export function parseNotesTerms(raw: string): string[] {
  let text = raw.replace(QUOTES, '"');
  if ((text.match(/"/g)?.length ?? 0) % 2 === 1) {
    const last = text.lastIndexOf('"');
    text = `${text.slice(0, last)} ${text.slice(last + 1)}`;
  }
  const seen = new Map<string, string>();
  for (const match of text.matchAll(TOKEN)) {
    const token = match[1] ?? match[2] ?? "";
    const term = stripEdges(token.split(/\s+/).filter(Boolean).join(" "));
    if (!Array.from(term).some(isAlnum)) continue;
    const key = foldNotesText(term);
    if (!seen.has(key)) seen.set(key, term);
    if (seen.size === MAX_NOTES_TERMS) break;
  }
  return Array.from(seen.values());
}

/** The folded text plus, per folded UTF-16 unit, the original range it came from. */
function foldWithMap(text: string): { folded: string; starts: number[]; ends: number[] } {
  let folded = "";
  const starts: number[] = [];
  const ends: number[] = [];
  let offset = 0;
  for (const char of text) {
    const out = foldChar(char);
    for (let i = 0; i < out.length; i++) {
      starts.push(offset);
      ends.push(offset + char.length);
    }
    folded += out;
    offset += char.length;
  }
  return { folded, starts, ends };
}

function termSource(term: string): string {
  let source = "";
  let previousSpace = false;
  for (const char of foldNotesText(term)) {
    if (/\s/.test(char)) {
      if (!previousSpace) source += "\\s+";
      previousSpace = true;
      continue;
    }
    previousSpace = false;
    source += SYNTAX.has(char) ? `\\${char}` : char;
  }
  return source;
}

// One compiled pattern per set of terms: every rationale cell on the page asks
// with the same terms, and the page re-renders on each 1.5 s poll. Keyed on
// the pattern's own source, so two term sets share it only if they compile to
// the same thing. It is a shared `g` RegExp: use it only through `matchAll`,
// which clones it, never `exec`/`test`, whose `lastIndex` would leak between
// cells.
let cached: RegExp | null = null;

function termsPattern(sources: string[]): RegExp {
  const source = `(?<![\\p{L}\\p{N}_])(?:${sources.join("|")})`;
  if (cached?.source !== source) cached = new RegExp(source, "gu");
  return cached;
}

/**
 * Where `terms` occur in `text`, as merged `[start, end)` ranges over the
 * ORIGINAL string, even where lowercasing changed a character's length.
 * A term counts only at the start of a word, like the server's `\m`.
 */
export function findNotesRanges(text: string, terms: readonly string[]): Array<[number, number]> {
  // Longest first: an alternation takes the FIRST alternative that matches, so
  // `fin|fintech` would mark only "fin" of "fintech".
  const sources = terms
    .map(termSource)
    .filter((s) => s.length > 0)
    .sort((a, b) => b.length - a.length);
  if (sources.length === 0 || text.length === 0) return [];
  const { folded, starts, ends } = foldWithMap(text);
  const pattern = termsPattern(sources);
  const ranges: Array<[number, number]> = [];
  for (const match of folded.matchAll(pattern)) {
    const at = match.index ?? 0;
    if (match[0].length === 0) continue;
    ranges.push([starts[at] as number, ends[at + match[0].length - 1] as number]);
  }
  ranges.sort((a, b) => a[0] - b[0]);
  const merged: Array<[number, number]> = [];
  for (const range of ranges) {
    const last = merged[merged.length - 1];
    if (last && range[0] <= last[1]) last[1] = Math.max(last[1], range[1]);
    else merged.push([range[0], range[1]]);
  }
  return merged;
}
